import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Breadcrumb, Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import { BsChevronLeft, BsInfoCircle, BsLock } from 'react-icons/bs';
import { Link, Outlet, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ClearanceStatusEnum,
  ConnectedFacilityService,
  ProviderService,
  ClearanceService,
  PatchedClearanceNotCleared,
} from '../../../api';
import { Card } from '../../../app/components/Card';
import { ClearanceControl } from '../../../app/components/ClearanceControl';
import { ConnectedFacilitiesList } from '../../../app/components/ConnectedFacilitiesList';
import { DenyClearanceDialog } from '../../../app/components/DenyClearanceDialog/DenyClearanceDialog';
import { ProviderProfileHeader } from '../../../app/components/ProviderProfileHeader';
import { TabsNavbar } from '../../../app/components/TabsNavbar';
import { SessionContext } from '../../../app/contexts/SessionContext';
import { useDocumentTitle } from '../../../app/hooks/use-document-title';
import { connectionRequestsQueryKeys } from '../../../app/query-keys-factories/connection-requests';
import { healthcareEntityFacilityQueryKeys } from '../../../app/query-keys-factories/facilities';
import { providerQueryKeys } from '../../../app/query-keys-factories/providers';
import { handleApiError } from '../../../app/utils/errorhandling';
import { CreateConnectionDialog } from './CreateConnectionDialog';

const ClearanceActions = ({
  provider,
  hasConnection,
  canChangeClearanceStatus,
  isChangingClearanceStatus,
  onClearanceStatusChange,
}: {
  provider: any;
  hasConnection: boolean;
  canChangeClearanceStatus: boolean;
  isChangingClearanceStatus: boolean;
  onClearanceStatusChange: (status: ClearanceStatusEnum) => void;
}) => {
  if (
    !provider ||
    !hasConnection ||
    !canChangeClearanceStatus ||
    !provider.clearance_status ||
    provider.clearance_status === ClearanceStatusEnum.CLEARED
  ) {
    return null;
  }

  return (
    <Col span="auto" sm={{ order: 2, span: 12 }} md={{ order: 1, span: 'auto' }}>
      <ClearanceControl
        isDisabled={isChangingClearanceStatus}
        clearanceStatus={provider.clearance_status as ClearanceStatusEnum}
        onClearanceStatusChange={onClearanceStatusChange}
      />
    </Col>
  );
};

const ConnectButton = ({
  user,
  isLoading,
  hasConnection,
  onCreateConnection,
  onAddFacility,
}: {
  user: any;
  isLoading: boolean;
  hasConnection: boolean;
  onCreateConnection: () => void;
  onAddFacility: () => void;
}) => {
  if (!user?.group_names?.includes('connect_with_providers') || isLoading) {
    return null;
  }

  return (
    <Col
      className="d-flex gap-3 align-items-center justify-content-end"
      sm={{ order: 1, span: 12 }}
      md={{ order: 2, span: 'auto' }}
    >
      {hasConnection ? (
        <Button size="lg" className="p-3 w-100" onClick={onAddFacility}>
          Connect
        </Button>
      ) : (
        <Button size="lg" className="p-3 w-100" onClick={onCreateConnection}>
          Connect
        </Button>
      )}
    </Col>
  );
};

const ProviderTabs = ({
  provider,
  providerPermissions,
}: {
  provider: any;
  providerPermissions: any;
}) => (
  <TabsNavbar
    alignment="center"
    tabs={[
      {
        label: 'Calendar Overview',
        to: `/app/healthcare-entity/providers/${provider.id}`,
      },
      {
        label: <>{!providerPermissions?.retrieve.details && <BsLock />} Credentials</>,
        to: `/app/healthcare-entity/providers/${provider.id}/credentials`,
        disabled: !providerPermissions?.retrieve.details,
      },
      {
        label: <>{!providerPermissions?.retrieve.details && <BsLock />} Professional Traits</>,
        to: `/app/healthcare-entity/providers/${provider.id}/professional-traits`,
        disabled: !providerPermissions?.retrieve.details,
      },
      {
        label: <>{!providerPermissions?.retrieve.details && <BsLock />} Education</>,
        to: `/app/healthcare-entity/providers/${provider.id}/education`,
        disabled: !providerPermissions?.retrieve.details,
      },
      {
        label: (
          <>{!providerPermissions?.retrieve.details && <BsLock />} Licenses & Certifications</>
        ),
        to: `/app/healthcare-entity/providers/${provider.id}/licenses`,
        disabled: !providerPermissions?.retrieve.details,
      },
      {
        label: <>{!providerPermissions?.retrieve.details && <BsLock />} Other</>,
        to: `/app/healthcare-entity/providers/${provider.id}/other`,
        disabled: !providerPermissions?.retrieve.details,
      },
    ]}
  />
);

export const ProviderProfile = () => {
  useDocumentTitle('Provider Profile');
  const { providerId: providerIdOrUndefined } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(SessionContext);
  const [showCreateConnectionDialog, setShowCreateConnectionDialog] = useState(false);
  const [showAddFacilityConnectionDialog, setShowAddFacilityConnectionDialog] = useState(false);
  const [showDenyClearanceDialog, setShowDenyClearanceDialog] = useState(false);

  const providerId = providerIdOrUndefined ?? '';
  const canChangeClearanceStatus = user?.group_names?.includes('grant_clearance');
  const canChangePresentationStatus = user?.group_names?.includes('grant_presentation_approval');

  const {
    data: providerPermissions,
    isLoading: isProviderPermissionsLoading,
    error: permissionsFetchError,
  } = useQuery({
    queryKey: providerQueryKeys.permissions(providerId),
    queryFn: async () =>
      await ProviderService.providerPermissionsRetrieve({
        providerId,
      }),
    enabled: !!providerId,
  });

  const {
    isLoading: isProviderLimitedLoading,
    error: providerLimitedFetchError,
    data: providerLimited,
  } = useQuery({
    queryKey: providerQueryKeys.limitedDetail(providerId),
    queryFn: async () =>
      await ProviderService.providerLimitedRetrieve({
        providerId: providerId,
      }),
    enabled:
      providerPermissions?.retrieve.details === false &&
      providerPermissions?.retrieve.limited_details,
  });

  const {
    isLoading: isProviderFullLoading,
    error: providerFullFetchError,
    data: providerFull,
  } = useQuery({
    queryKey: providerQueryKeys.detail(providerId),
    queryFn: async () =>
      await ProviderService.providerRetrieve({
        providerId: providerId,
      }),
    enabled: providerPermissions?.retrieve.details,
  });

  const connectionQueryParams: Parameters<
    typeof ConnectedFacilityService.connectedFacilityList
  >[0] = {
    provider: [providerId],
    ordering: 'facility__name',
  };
  const { data: connectionData, isLoading: isConnectionDataLoading } = useQuery({
    queryKey: connectionRequestsQueryKeys.list(connectionQueryParams),
    queryFn: async () =>
      await ConnectedFacilityService.connectedFacilityList(connectionQueryParams),
    enabled: !!providerId,
  });

  const provider = providerPermissions?.retrieve.details ? providerFull : providerLimited;
  const isProviderLoading = providerPermissions?.retrieve.details
    ? isProviderFullLoading
    : isProviderLimitedLoading;
  const providerFetchError = providerPermissions?.retrieve.details
    ? providerFullFetchError
    : providerLimitedFetchError;

  const hasConnection = useMemo(() => {
    if (isConnectionDataLoading || !connectionData?.results) {
      return false;
    }
    return connectionData.results.length > 0;
  }, [connectionData?.results, isConnectionDataLoading]);

  useEffect(() => {
    if (!providerIdOrUndefined) {
      navigate('/app');
    }
  }, [providerIdOrUndefined, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const queryClient = useQueryClient();
  const invalidateQueries = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries(connectionRequestsQueryKeys.allLists()),
      queryClient.invalidateQueries(providerQueryKeys.allLists()),
      queryClient.invalidateQueries(providerQueryKeys.allDetails()),
      queryClient.invalidateQueries(healthcareEntityFacilityQueryKeys.allLists()),
    ]);
  }, [queryClient, providerId]);

  const mutateMarkCleared = useMutation({
    mutationFn: async () => {
      const results = connectionData!.results!;
      await ClearanceService.clearanceClearedPartialUpdate({
        id: results[0].clearance_id,
      });
    },
    onSuccess: async () => {
      await invalidateQueries();
    },
  });

  const mutateMarkIsPendingClearance = useMutation({
    mutationFn: async () => {
      const results = connectionData!.results!;
      await ClearanceService.clearancePendingPartialUpdate({
        id: results[0].clearance_id,
      });
    },
    onSuccess: async () => {
      await invalidateQueries();
    },
  });

  const mutateMarkIsNotCleared = useMutation({
    mutationFn: async (data: PatchedClearanceNotCleared) => {
      const results = connectionData!.results!;
      const formattedData = {
        ...data,
        clearance_date: data.clearance_date
          ? new Date(data.clearance_date).toISOString()
          : undefined,
      };
      await ClearanceService.clearanceNotClearedPartialUpdate({
        id: results[0].clearance_id,
        requestBody: formattedData,
      });
    },
    onSuccess: async () => {
      await invalidateQueries();
    },
  });

  const markCleared = async () => {
    try {
      await mutateMarkCleared.mutateAsync();

      toast.success('Provider marked as cleared.');
    } catch (errors) {
      handleApiError(errors);
    }
  };

  const markPendingClearance = async () => {
    try {
      await mutateMarkIsPendingClearance.mutateAsync();

      toast.success('Provider marked as pending clearance.');
    } catch (errors) {
      handleApiError(errors);
    }
  };

  const markNotCleared = async (data: PatchedClearanceNotCleared) => {
    try {
      await mutateMarkIsNotCleared.mutateAsync(data);

      toast.success('Provider marked as not cleared.');
    } catch (errors) {
      handleApiError(errors);
    }
  };

  const handleDenyClearance = (result) => {
    setShowDenyClearanceDialog(false);

    if (result) {
      markNotCleared(result);
    }
  };

  const onClearanceStatusChangeHandler = (status: ClearanceStatusEnum) => {
    switch (status) {
      case ClearanceStatusEnum.CLEARED:
        markCleared();
        break;
      case ClearanceStatusEnum.NOT_CLEARED:
        setShowDenyClearanceDialog(true);
        break;
      case ClearanceStatusEnum.PENDING_CLEARANCE:
        markPendingClearance();
        break;
      default:
        break;
    }
  };

  const isChangingClearanceStatus = useMemo(() => {
    return (
      mutateMarkCleared.isLoading ||
      mutateMarkIsPendingClearance.isLoading ||
      mutateMarkIsNotCleared.isLoading
    );
  }, [
    mutateMarkCleared.isLoading,
    mutateMarkIsNotCleared.isLoading,
    mutateMarkIsPendingClearance.isLoading,
  ]);

  return (
    <>
      <div>
        <Container className="py-4 d-flex justify-content-start align-items-center">
          <Button
            className="me-3 mb-3 d-inline-flex align-items-center"
            size="sm"
            onClick={() => navigate(-1)}
          >
            <BsChevronLeft className="me-2" /> Back
          </Button>
          <Breadcrumb className="align-items-center">
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/app/healthcare-entity/providers' }}>
              Providers List
            </Breadcrumb.Item>
            <Breadcrumb.Item active> Provider Profile </Breadcrumb.Item>
          </Breadcrumb>
        </Container>
      </div>
      <div style={{ position: 'relative' }}>
        <div
          className="bg-primary-dark d-block"
          style={{
            height: 'calc(3rem + 120px)',
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            zIndex: -10,
          }}
        />
        <Container>
          <Row>
            <Col xs={12}>
              {!isProviderLoading &&
                !isProviderPermissionsLoading &&
                (!!providerFetchError || !!permissionsFetchError) && (
                  <Row className="row-cols-auto justify-content-center">
                    <Col>
                      <Alert variant="danger" show={!!providerFetchError}>
                        <Alert.Heading>Something went wrong</Alert.Heading>
                        <p>Please try again later. If the problem persists, please contact us.</p>
                      </Alert>
                    </Col>
                  </Row>
                )}
              <ProviderProfileHeader
                provider={provider}
                isProviderLoading={isProviderPermissionsLoading || isProviderLoading}
                hasAcceptedConnection={
                  (provider?.clearance_status &&
                    provider?.clearance_status !== ClearanceStatusEnum.NOT_CLEARED) ||
                  false
                }
                callToAction={
                  <Row className="h-100 d-flex gap-3 align-items-center justify-content-xl-end pb-3">
                    <ClearanceActions
                      provider={provider}
                      hasConnection={hasConnection ?? false}
                      canChangeClearanceStatus={canChangeClearanceStatus ?? false}
                      isChangingClearanceStatus={isChangingClearanceStatus}
                      onClearanceStatusChange={onClearanceStatusChangeHandler}
                    />
                    <ConnectButton
                      user={user}
                      isLoading={isProviderLoading || isProviderPermissionsLoading}
                      hasConnection={hasConnection ?? false}
                      onCreateConnection={() => setShowCreateConnectionDialog(true)}
                      onAddFacility={() => setShowAddFacilityConnectionDialog(true)}
                    />
                  </Row>
                }
              />
              {!!provider &&
                !isProviderPermissionsLoading &&
                providerPermissions?.retrieve.limited_details && (
                  <ConnectedFacilitiesList
                    canChangePresentationStatus={canChangePresentationStatus}
                    isNotCleared={provider.clearance_status !== ClearanceStatusEnum.CLEARED}
                    providerId={provider.id}
                  />
                )}
              <Card className="mb-4">
                {(isProviderLoading || isProviderPermissionsLoading) && (
                  <Row>
                    <Col xs={12} className="d-flex align-items-center justify-content-center">
                      <Spinner
                        aria-label="loading indicator"
                        animation="grow"
                        className="loading-spinner"
                        variant="primary"
                      />
                    </Col>
                  </Row>
                )}

                {!!provider && (
                  <>
                    <ProviderTabs provider={provider} providerPermissions={providerPermissions} />
                    <div className="d-flex flex-column position-relative">
                      <Outlet />
                      {!providerPermissions?.retrieve.details && (
                        <div className="d-flex flex-column position-absolute connection-screen justify-content-center align-items-center">
                          <div className="text-center connection-screen--text">
                            <BsInfoCircle size={48} className="mb-3 mx-auto" />
                            <div className="fs-4 mb-3 pb-2">
                              Create connection with a provider to view a provider&apos;s
                              availability!
                              <ConnectButton
                                user={user}
                                isLoading={isProviderLoading || isProviderPermissionsLoading}
                                hasConnection={hasConnection ?? false}
                                onCreateConnection={() => setShowCreateConnectionDialog(true)}
                                onAddFacility={() => setShowAddFacilityConnectionDialog(true)}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      {!isConnectionDataLoading && !!provider && (
        <>
          <CreateConnectionDialog
            provider={provider}
            isDialogOpen={showCreateConnectionDialog}
            closeDialog={() => setShowCreateConnectionDialog(false)}
            hasConnection={false}
          />
          <CreateConnectionDialog
            provider={provider}
            isDialogOpen={showAddFacilityConnectionDialog}
            closeDialog={() => setShowAddFacilityConnectionDialog(false)}
            hasConnection={true}
          />
          <DenyClearanceDialog show={showDenyClearanceDialog} closeDialog={handleDenyClearance} />
        </>
      )}
    </>
  );
};
